<template>
  <ModalSide class="product-modal" ref="modal">
    <Scrollbar :key="refrash_key">
      <div class="sticky-head">
        <header>
          <!-- Product Performance -->
          <div class="title">
            {{ user_info.has ? 'Product Performance by Creator' : 'Product Performance' }}
            <span v-if="user_info.has" @click="openProfile(user_info.data)">
              <figure class="avatar">
                <img
                  v-if="user_info.data.userProfile"
                  :src="user_info.data.userProfile.avatar_full_url"
                />
                <img v-else src="@/assets/images/user.png" />
              </figure>
              {{ user_info.data.first_name }} {{ user_info.data.last_name }}
            </span>
          </div>
          <div class="icon-box">
            <a
              class="icon i-1"
              v-if="product_data"
              target="_blank"
              :href="product_data.product_url"
              v-tippy="{ content: 'Open product source in new tab', delay: [500, 20] }"
              >View Product
              <SvgIcon name="share" />
            </a>
            <div
              class="icon i-2"
              v-tippy="{
                content: 'Download product report summaries in CSV format',
                placement: 'right',
                delay: [500, 20],
              }"
              @click="downloadCsv()"
            >
              <SvgIcon name="download2" />
            </div>
            <div class="icon i-3" @click="close">
              <SvgIcon name="close" />
            </div>
          </div>
        </header>
        <div class="navi-line-block">
          <Navi :menu="menu" :type="1" @selected-tab="selectTab" />
          <div class="title">
            <Dropdown
              v-if="can_toggle_sortlink && !show_last_datetime && !is_link_only"
              :items="data_source_items"
              :direction_type="'left'"
              :icon_direction_type="'right'"
              :default_value="data_source_default_index"
              :key="'bot-' + sortlink_data"
              @update-dropdown="onDataSourceChange"
            />
            <div class="update-time" v-if="can_toggle_sortlink && show_last_datetime">
              Last Update: {{ store.state.user_data.merchant_data_update_hour_ago }}h ago
            </div>
            <DatePicker
              v-if="!is_hide_date"
              v-show="!is_loading_data && show_date_picker"
              @update-date="updateDate"
              :date_range="date_range"
              ref="date_picker"
            />
            <el-button
              class="the-curated-btn"
              @click="ClickCurated"
              v-if="now_merchant.is_enable_storefront_curation && !is_link_only"
            >
              <Checkbox size="small" :checked="curated_check" />
              Curated only
            </el-button>
          </div>
        </div>
      </div>

      <div class="box" v-if="product_data" v-show="active_tab === 0">
        <div class="product-info-box">
          <figure class="image-wrap">
            <img class="product-img" :src="product_data.images.img_400x400" />
          </figure>
          <div class="product-info">
            <div class="product-flex-box">
              <div>
                <div
                  class="product-brand"
                  v-html="product_data && product_data.brand_name ? product_data.brand_name : ''"
                ></div>
                <div class="product-name" v-html="product_data.name"></div>
              </div>
              <div class="product-btn" @click="goFullReport" v-if="user_info.has">Full Product Report</div>
            </div>
            <div class="product-data-box">
              <div class="product-data">
                <div class="text">Price</div>
                <div class="value">
                  ${{
                    product_data.on_sale === 1 ? formatMoney(product_data.sale_price) : formatMoney(product_data.price)
                  }}
                </div>
              </div>
              <div class="product-data" v-if="store.state.show_advertisement">
                <div class="text">Advertiser</div>
                <div class="value">{{ product_data.advertiser_name }}</div>
              </div>
              <div class="product-data">
                <div class="text">Category</div>
                <div class="value">{{ product_data.category_name }}</div>
              </div>
              <div class="product-data">
                <div class="text">Availability</div>
                <div class="value">{{ available_text }}</div>
              </div>
              <div class="product-data" v-if="user_info.has">
                <div class="text">Added Date</div>
                <div class="value">{{ $filters.momentFormat(product_data.created_at, 'll') }}</div>
              </div>
              <div class="product-data" v-if="user_info.has">
                <div class="text">Last Update</div>
                <div class="value">{{ $filters.momentFormat(product_data.updated_at, 'll') }}</div>
              </div>
            </div>
          </div>
        </div>
        <!---------------------------- chart ------------------------------------->
        <StorefrontSummary
          ref="storefront_summary"
          :show_date_picker="false"
          :user_id="user_id"
          :date_range="date_range"
          :product_id="product_id"
          :api_url="summary_api_url"
          :title="summary_title"
          :selected_creator_tags="selected_tags_for_api"
          :creator_tag_operation_type="tag_operation_type"
          :init_sortlink="sortlink_data"
          :one_product="true"
          :is_link_only="is_link_only"
          :no_title="true"
          :user_data="user_info.data"
          :no_dropdown="true"
        />
        <!---------------------------- for all ----------------------------------->
        <div v-if="!user_info.has">
          <div :class="['report-box', { 'is-link-user-box': is_link_only }]" v-if="data_sta && !is_loading_data">
            <TopBox
              v-if="show_views"
              :data="{
                title: 'Top Product Views',
                user: data_sta.top_view_user,
                icon: 'eye',
              }"
              @close="close"
            />

            <TopBox
              :data="{
                title: 'Top Clicks',
                user: data_sta.top_click_sum_user,
                icon: 'click',
              }"
              @close="close"
            />

            <TopBox
              :data="{
                title: 'Top Sales',
                user: data_sta.top_sales_sum_user,
                icon: 'pig',
              }"
              @close="close"
            />

            <TopBox
              :data="{
                title: 'Top Sales Value',
                user: data_sta.top_sales_value,
                icon: 'shopping-cart',
              }"
              @close="close"
            />
          </div>
          <div>
            <FilterResults :params="params" @update-filter="updateFilter" />
          </div>
          <!-- table -->
          <Loading class="static" v-if="is_loading" />
          <Scrollbar class="data-list" v-else>
            <Table :style="top" :class="{ 'is-loading': is_loading_data }">
              <template #head>
                <tr>
                  <TableTh name="name" title="Name" :sort_value="sortValue" @sort="sort" content="Name" />
                  <th><span>Socials</span></th>
                  <TableTh
                    name="anchor_status"
                    title="Status"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="This is the current status of each creator's storefront with the date that status updated. Please hover over the status to learn more."
                  />
                  <TableTh
                    v-if="show_views"
                    name="product_views"
                    title="Product Views"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="Cumulative product views for the time period selected."
                  />
                  <TableTh
                    name="click"
                    title="Clicks"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="Total product link clicks for the time period selected."
                  />
                  <TableTh
                    name="sales"
                    title="Sales"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="Sales driven by the storefront for the time period selected."
                  />
                  <TableTh
                    name="sales_value"
                    title="Sales Value"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="Gross transaction value of sales driven by storefront in the time period selected."
                  />
                  <TableTh
                    name="add_date"
                    title="Added"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="Date this product was added to the creator's storefront."
                  />
                  <TableTh
                    name="last_update_date"
                    title="Last Update"
                    :sort_value="sortValue"
                    @sort="sort"
                    content="This date will change when a creator adds or edits products on their storefront."
                  />
                </tr>
              </template>
              <template #body>
                <template v-if="!is_loading && data.length === 0">
                  <tr>
                    <td :colspan="`${show_views ? 10 : 9}`">
                      <NoData />
                    </td>
                  </tr>
                </template>
                <tr v-else v-for="(row, index) in data" :key="index" :class="['tbody-tr']" @click.stop="pickUser(row)">
                  <td class="name">
                    <div class="profile">
                      <img
                        v-if="row.userProfile"
                        :src="row.userProfile.avatar_full_url"
                        @error="imageError($event)"
                        @click.stop="goProfile(row)"
                      />
                      <img v-else src="@/assets/images/user.png" @click.stop="goProfile(row)" />

                      <div class="name-text-box">
                        <tippy
                          :content="showFullText(row, 'name')"
                          @click.stop="goProfile(row)"
                          @mouseover="isEllipsisActive($event, row)"
                          class="name"
                        >
                          <strong class="text-ellipsis" v-html="row.first_name + ' ' + row.last_name"></strong>
                        </tippy>
                        <div class="email-box" v-if="store.state.user_data.is_motom_internal_user">
                          <svg-icon name="email" />
                          <tippy :content="showFullText(row, 'email')" @mouseover="isEllipsisActive($event, row)">
                            <span v-if="row.email" class="link">
                              <span class="text-ellipsis" @click.stop="emailto(row.email)">{{ row.email }}</span></span
                            >
                            <span class="link" v-else> ---</span><br />
                          </tippy>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td class="social">
                    <tippy
                      :content="showFullText(row, 'tt')"
                      @mouseover="isEllipsisActive($event, row)"
                      @click.stop="GoMedia('tk', row.tiktok_handle)"
                    >
                      <span v-if="row.tiktok_handle" class="text-ellipsis link text-ellipsis-link">
                        <SvgIcon name="tiktok-pure" /> {{ row.tiktok_handle }}
                        <b v-if="row.is_tiktok_handle_updated">Updated</b>
                      </span>
                      <span v-else> <SvgIcon name="tiktok-pure" /> --- </span><br />
                    </tippy>
                    <tippy
                      :content="showFullText(row, 'ins')"
                      @mouseover="isEllipsisActive($event, row)"
                      @click.stop="GoMedia('ig', row.instagram_handle)"
                    >
                      <span v-if="row.instagram_handle" class="text-ellipsis link text-ellipsis-link">
                        <SvgIcon name="instagram" /> @{{ row.instagram_handle }}
                        <b v-if="row.is_instagram_handle_updated">Updated</b>
                      </span>
                      <span v-else> <SvgIcon name="instagram" /> --- </span>
                    </tippy>
                  </td>

                  <td>
                    <tippy
                      :delay="[0, 20]"
                      class="storefront-state"
                      :content="
                        showStageText(row.anchor_page_status, $filters.momentFormat(row.show_status_time, 'll'))
                      "
                      :class="['class-' + row.anchor_page_status]"
                    >
                      {{ row.anchor_page_status_text }}
                    </tippy>
                    <p class="status_text_time">{{ $filters.momentFormat(row.show_status_time, 'll') }}</p>
                  </td>
                  <td v-if="show_views">
                    <span
                      v-if="!isNaN(row.product_views) && row.product_views !== null && row.product_views !== undefined"
                      >{{ Number(row.product_views).toLocaleString('en-US') }}</span
                    >
                    <span v-else>---</span>
                  </td>
                  <td>
                    <div class="td-flex">
                      <span v-if="!isNaN(row.click) && row.click !== null && row.click !== undefined">{{
                        Number(row.click).toLocaleString('en-US')
                      }}</span>
                      <span v-else>---</span>
                      <i class="lig" v-if="store.state.save_sortlink_data === 'all' && row.click_include_shortlink_data"
                        ><svg-icon name="lightning"
                      /></i>
                    </div>
                  </td>
                  <td>
                    <div class="td-flex">
                      <span v-if="!isNaN(row.sales) && row.sales !== null && row.sales !== undefined">{{
                        Number(row.sales).toLocaleString('en-US')
                      }}</span>
                      <span v-else>---</span>
                      <i class="lig" v-if="store.state.save_sortlink_data === 'all' && row.sales_include_shortlink_data"
                        ><svg-icon name="lightning"
                      /></i>
                    </div>
                  </td>
                  <td>
                    <div class="td-flex">
                      <span v-if="!isNaN(row.sales_value)"
                        >${{
                          Number(row.sales_value).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        }}</span
                      >
                      <span v-else>---</span>
                      <i class="lig" v-if="store.state.save_sortlink_data === 'all' && row.sale_value_include_shortlink_data"
                        ><svg-icon name="lightning"
                      /></i>
                    </div>
                  </td>
                  <td>
                    {{ $filters.momentFormat(row.store_front_created_at, 'll') }}
                  </td>
                  <td>
                    {{ $filters.momentFormat(row.last_store_front_update_at, 'll') }}
                  </td>
                </tr>
              </template>
              <template #foot v-if="data_sum && data_sum.row_count > 0 && search_keyword === '' && !has_filters">
                <tr>
                  <td colspan="2">Total {{ data_sum.row_count }}</td>
                  <td>
                    {{ data_sum.anchor_active_count }} Active <br />
                    {{ data_sum.anchor_disabled_count }} Disabled
                  </td>
                  <td v-if="show_views">{{ formatNumber(data_sum.product_view_total) }}</td>
                  <td>{{ formatNumber(data_sum.clicks_total) }}</td>
                  <td>{{ formatNumber(data_sum.sales_total) }}</td>
                  <td>
                    ${{
                      Number(data_sum.sales_value_total).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}
                  </td>
                  <td colspan="2"></td>
                </tr>
              </template>
            </Table>
          </Scrollbar>
        </div>
      </div>

      <ProductModalCreators
        v-if="active_tab === 1 && product_data"
        :product_id="product_id"
        :start_date="start_date"
        :end_date="end_date"
        :sortlink_data="sortlink_data"
        :curated_check="curated_check"
        :is_link_only="is_link_only"
        @select-tab="selectTab"
        @go-profile="goProfileOverview"
      />
    </Scrollbar>
  </ModalSide>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue';

const props = defineProps({
  is_nested: { type: Boolean, default: false },
});
import { useStore } from 'vuex';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import Loading from '@/components/ui/Loading.vue';
import ModalSide from '@/components/ui/ModalSide.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Checkbox from '@/components/ui/Checkbox.vue';
import formatMoneyFn from '@/utils/formatMoney';
import moment from 'moment';
import TopBox from '@/components/product/TopBox.vue';
import StorefrontSummary from '@/components/summary/StorefrontSummary.vue';
import ProductModalCreators from '@/components/product/ProductModalCreators.vue';
import { goMedia } from '@/utils/goMedia';
import { showStageText } from '@/utils/ShareEvent';
import { dataSourceToApi } from '@/utils/SwitchFun.js';
import Navi from '@/components/Navi.vue';
import { ElButton } from 'element-plus';
import defaultAvatar from '@/assets/images/user.png';

const store = useStore();
const { proxy } = getCurrentInstance();
const $ajax = proxy.$ajax;
const $notify = proxy.$notify;
const $JsDownload = proxy.$JsDownload;
const EventBus = proxy.EventBus;

// Template refs
const modal = ref(null);
const storefront_summary = ref(null);
const active_tab = ref(0);

// Data
const sort_key = ref('product_views');
const sort_val = ref('desc');
const start_date = ref('');
const end_date = ref('');
const product_id = ref('');
const date_range = reactive({});
const date_range_summary = reactive({});
const product_data = ref(null);
const data = ref([]);
const data_sta = ref(null);
const data_sum = ref(null);
const top = ref('--top: 0px');
const is_loading = ref(true);
const is_loading_data = ref(false);
const search_keyword = ref('');
const top_views = ref(false);
const top_sales = ref(false);
const top_clicks = ref(false);
const top_sales_value = ref(false);
const total_views = ref(0);
const total_clicks = ref(0);
const total_sales = ref(0);
const total_sales_value = ref(0);
const select_pagenum = [
  { id: 50, text: '50/page' },
  { id: 20, text: '20/page' },
];
const page_size = ref(0);
const page_total = ref(0);
const page_current = ref(1);
const is_empty_data = ref(false);
const params = reactive({
  profile_status: 0,
  storefront_status: 0,
  recently_added: 0,
  metric: 0,
  metric_type: 0,
  metric_amount: '',
  metric_min_amount: '',
  metric_max_amount: '',
  social: {
    metric: 1,
    metric_msg: '',
    metric_min_amount_input: '',
    metric_max_amount_input: '',
  },
});
const has_filters = ref(false);
const user_info = reactive({
  has: false,
  data: null,
});
const refrash_key = ref(0);
const sortlink_data = ref('all');
const curated_check = ref(false);
const show_date_picker = ref(true);
const is_hide_date = ref(false);
const show_last_datetime = ref(false);
const date_picker = ref(null);
const data_source_items = [
  { text: 'All Data',       value: 'all' },
  { text: 'Storefront Data', value: 'storefront' },
  { text: 'Shortlink Data',  value: 'shortlink' },
];
const product = ref(null);
const menu = ref([{ text: 'Overview' }, { text: 'Creators' }]);

// Computed
const is_user_link_only = computed(() => {
  return user_info.data?.platform_type === 'linkonly';
});

const is_link_only = computed(() => {
  const merchant_is_link_only = store.state.user_data?.merchant_account.platform_type === 'linkonly';
  if (merchant_is_link_only && store.state.list_has_historical_storefront_tracking ) return is_user_link_only.value;
  return merchant_is_link_only || is_user_link_only.value;
});

const show_views = computed(() => {
  return !is_link_only.value && sortlink_data.value !== 'shortlink';
});

const available_text = computed(() => {
  if (product_data.value.src_type == 2) return 'Unknown';
  else if (product_data.value.availability === 1) return 'In stock';
  else return 'Not in stock';
});

const user_id = computed(() => {
  if (user_info.has && user_info.data) return user_info.data.id;
  else return '';
});

const summary_api_url = computed(() => {
  if (user_info.has) return '/creator/creator-product-detail-trend-detail';
  else return '/analytics/product-detail-trend-detail';
});

const summary_title = computed(() => {
  return {
    title: 'Product Summary',
    view: 'Product Views',
    click: 'Clicks',
    sale: 'Sales',
    sale_value: 'Sales Value',
  };
});

const selected_tags_for_api = computed(() => {
  return store.state.save_selected_tags || [];
});

const tag_operation_type = computed(() => {
  return store.state.save_tag_operation_type || 'or';
});

const sortValue = computed(() => ({
  sort_key: sort_key.value,
  sort_val: sort_val.value,
}));

const can_toggle_sortlink = computed(() => !is_link_only.value);

const data_source_default_index = computed(() =>
  data_source_items.findIndex((item) => item.value === sortlink_data.value)
);

const now_merchant = computed(() => store.state.user_data.merchant_account);

//+++++++++++++++++++++++++++++++++
// Methods
//+++++++++++++++++++++++++++++++++
const GoMedia = goMedia;

const selectTab = (id) => {
  active_tab.value = id;
};

const sortLinkClick = (val) => {
  sortlink_data.value = val;
  statisticData();
  getTable();
  getSum();
};

const onDataSourceChange = (item) => {
  sortlink_data.value = item.value;
  statisticData();
  getTable();
  getSum();
  if (storefront_summary.value) {
    storefront_summary.value.sortlink_data = item.value;
    storefront_summary.value.chart_type = item.value === 'shortlink' ? 'click' : 'view';
    storefront_summary.value.getData();
  }
};

const ClickCurated = () => {
  curated_check.value = !curated_check.value;
  statisticData();
  getTable();
  getSum();
  if (storefront_summary.value) {
    storefront_summary.value.curated_check = curated_check.value;
    storefront_summary.value.getData();
  }
};

const showFullText = (row, type) => {
  let text = '';
  if (row.is_ellipsis_active) {
    switch (type) {
      case 'name':
        text = row.last_name + ' ' + row.last_name;
        break;
      case 'ins':
        text = row.instagram_handle;
        if (row.is_instagram_handle_updated) text += ' (Updated)';
        break;
      case 'tt':
        text = row.tiktok_handle;
        if (row.is_tiktok_handle_updated) text += ' (Updated)';
        break;
      case 'email':
        text = row.email;
        break;
    }
  }
  return text;
};

const isEllipsisActive = (e, row) => {
  row.is_ellipsis_active = e.target.offsetWidth < e.target.scrollWidth;
};

const formatNumber = (num) => {
  return num ? num.toLocaleString('en-US') : '---';
};

const formatMoney = formatMoneyFn;

const open = () => {
  is_loading.value = true;
  active_tab.value = 0;
  store.state.product_data_open = true;
  sort_key.value = is_link_only.value ? 'click' : 'product_views';
  getInfo();
  statisticData();
  getTable();
  getSum();
  modal.value.open();
};

const close = () => {
  store.state.product_data_open = false;
  modal.value.close();
};

const getInfo = () => {
  let api = `/creator/get-product-detail?product_id=${product_id.value}&start_date=${start_date.value}&end_date=${end_date.value}`;

  if (user_info.data && user_info.data.id) api = `${api}&creator_user_id=${user_info.data.id}`;

  // add tag params
  if (store.state.save_selected_tags && store.state.save_selected_tags.length > 0) {
    store.state.save_selected_tags.forEach((tag) => {
      api += `&creator_tag_ids[]=${tag.id}`;
    });
  }
  if (store.state.save_tag_operation_type) {
    api += `&operation_type=${store.state.save_tag_operation_type}`;
  }

  $ajax(api, {
    method: 'get',
    withCredentials: true,
  }).then((res) => {
    // product_data.value = res.data;
    product_data.value = res.data.data;

  });
};

const updateDate = (obj) => {
  start_date.value = moment(obj.start_date).format('YYYY-MM-DD');
  end_date.value = moment(obj.end_date).format('YYYY-MM-DD');
  date_range.start_date = start_date.value;
  date_range.end_date = end_date.value;
  if (obj.curated_check !== undefined) {
    curated_check.value = obj.curated_check;
  }
  statisticData();
  getTable();
  getSum();
  if (storefront_summary.value) {
    storefront_summary.value.start_date = start_date.value;
    storefront_summary.value.end_date = end_date.value;
    storefront_summary.value.getData();
  }
};

const statisticData = () => {
  data_sta.value = null;
  let api = `/analytics/product-detail-statistic?product_id=${product_id.value}&start_date=${
    start_date.value
  }&end_date=${end_date.value}&is_include_shortlink_data=${dataSourceToApi(
    sortlink_data.value
  )}&is_include_curation_data=${curated_check.value ? '1' : '0'}`;

  // add tag params
  if (store.state.save_selected_tags && store.state.save_selected_tags.length > 0) {
    store.state.save_selected_tags.forEach((tag) => {
      api += `&creator_tag_ids[]=${tag.id}`;
    });
  }
  if (store.state.save_tag_operation_type) {
    api += `&operation_type=${store.state.save_tag_operation_type}`;
  }

  $ajax(api, {
    method: 'get',
    withCredentials: true,
  }).then((res) => {
    data_sta.value = res.data;
  });
};

const getApiParamStr = (api) => {
  if (search_keyword.value) api = `${api}&keyword=${search_keyword.value}`;

  api = `${api}&per_page=${page_size.value}&page=${page_current.value}&start_date=${start_date.value}&end_date=${end_date.value}`;

  // add tag params
  if (store.state.save_selected_tags && store.state.save_selected_tags.length > 0) {
    store.state.save_selected_tags.forEach((tag) => {
      api += `&creator_tag_ids[]=${tag.id}`;
    });
  }
  if (store.state.save_tag_operation_type) {
    api += `&operation_type=${store.state.save_tag_operation_type}`;
  }

  let filters = [];
  if (params.profile_status === 1) filters.push({ field_name: 'creator_status', value: 'joined' });
  if (params.profile_status === 2) filters.push({ field_name: 'creator_status', value: 'pending' });
  if (params.profile_status === 3) filters.push({ field_name: 'creator_status', value: 'deleted' });
  if (params.storefront_status === 1) filters.push({ field_name: 'anchor_status', value: 'active' });
  if (params.storefront_status === 2) filters.push({ field_name: 'anchor_status', value: 'pending' });
  if (params.storefront_status === 3) filters.push({ field_name: 'anchor_status', value: 'disabled' });
  if (params.storefront_status === 4) filters.push({ field_name: 'anchor_status', value: 'declined' });
  if (params.storefront_status === 5) filters.push({ field_name: 'anchor_status', value: 'invited' });
  if (params.recently_added === 1) filters.push({ field_name: 'recently_added', value: 'recently_added' });
  if (params.metric_amount !== '' || (params.metric_min_amount !== '' && params.metric_max_amount !== '')) {
    let metric_txt = '';
    switch (params.metric) {
      case 0:
        metric_txt = 'product_views';
        break;
      case 3:
        metric_txt = 'click';
        break;
      case 2:
        metric_txt = 'sales';
        break;
      case 1:
        metric_txt = 'sales_value';
        break;
      default:
        break;
    }
    if (params.metric_type === 0) filters.push({ field_name: metric_txt, operator: '>', value1: params.metric_amount });
    else if (params.metric_type === 1)
      filters.push({ field_name: metric_txt, operator: '<', value1: params.metric_amount });
    else filters.push({ field_name: metric_txt, value1: params.metric_min_amount, value2: params.metric_max_amount });
  }

  //new filter
  if (params.social.metric_max_amount_input && params.social.metric_min_amount_input) {
    let whi = params.social.metric === 1 ? 'follower_filter_tikTok' : 'follower_filter_instagram';
    filters.push({
      field_name: whi,
      value1: params.social.metric_min_amount_input,
      value2: params.social.metric_max_amount_input,
    });
  }

  if (filters.length > 0) {
    has_filters.value = true;
    api = `${api}&filters=${JSON.stringify(filters)}`;
  } else has_filters.value = false;

  return api;
};

const getTable = () => {
  if (!is_loading.value) is_loading_data.value = true;
  let api = `/analytics/product-performance-by-creators?product_id=${
    product_id.value
  }&is_include_shortlink_data=${dataSourceToApi(sortlink_data.value)}&is_include_curation_data=${
    curated_check.value ? '1' : '0'
  }`;

  if (sort_key.value) api = `${api}&sort_key=${sort_key.value}&sort_val=${sort_val.value}`;

  api = getApiParamStr(api);

  $ajax(api, {
    method: 'get',
    withCredentials: true,
    data: {},
  }).then((res) => {
    data.value = res.data.data.data;
    data.value = data.value.map((e) => {
      let show_status_time = '';
      switch (e.anchor_page_status) {
        case 100:
          show_status_time = e.accept_store_front_datetime;
          break;
        case 0:
          show_status_time = e.disabled_at;
          break;
        case -10:
          show_status_time = e.declined_at;
          break;
        case 10:
          show_status_time = e.unavailable_at;
          break;
        case 20:
          show_status_time = e.created_at;
          break;
      }

      return {
        ...e,
        show_status_time: show_status_time,
      };
    });
    page_total.value = res.data.data.last_page;
    page_current.value = res.data.data.current_page;
    total_views.value = res.data.total_views;
    total_clicks.value = res.data.total_clicks;
    total_sales.value = res.data.total_sales;
    total_sales_value.value = res.data.total_sales_value;
    let c_data = JSON.parse(JSON.stringify(data.value));
    if (c_data.length !== 0) {
      top_views.value = c_data.sort((a, b) => {
        return b.views - a.views;
      })[0];
      top_clicks.value = c_data.sort((a, b) => {
        return b.clicks - a.clicks;
      })[0];
      top_sales.value = c_data.sort((a, b) => {
        return b.sales - a.sales;
      })[0];
      top_sales_value.value = c_data.sort((a, b) => {
        return b.sales_value - a.sales_value;
      })[0];
    } else {
      top_views.value = null;
      top_clicks.value = null;
      top_sales.value = null;
      top_sales_value.value = null;
    }

    is_loading.value = false;
    is_loading_data.value = false;
    let has_params =
      params.profile_status !== 0 ||
      params.storefront_status !== 0 ||
      params.recently_added !== 0 ||
      ((params.metric_type === 0 || params.metric_type === 1) && params.metric_amount !== '') ||
      (params.metric_type === 2 && params.metric_min_amount !== '' && params.metric_max_amount !== '');
    is_empty_data.value = !is_loading.value && data.value.length === 0 && search_keyword.value === '' && !has_params;
  });
};

const getSum = () => {
  let api = `/analytics/product-performance-by-creators-sum?product_id=${
    product_id.value
  }&is_include_shortlink_data=${dataSourceToApi(sortlink_data.value)}&is_include_curation_data=${
    curated_check.value ? '1' : '0'
  }`;

  api = getApiParamStr(api);

  $ajax(api, {
    method: 'get',
    withCredentials: true,
  }).then((res) => {
    data_sum.value = res.data.data;
  });
};

const downloadCsv = () => {
  let api_url = `/analytics/get-product-performance-by-creators-csv-file?product_id=${product_id.value}`;
  let api = getApiParamStr(api_url);
  if (sort_key.value) api = `${api}&sort_key=${sort_key.value}&sort_val=${sort_val.value}`;
  if (search_keyword.value) api = `${api}&keyword=${search_keyword.value}`;

  $ajax(api, {
    method: 'get',
  }).then((res) => {
    if (res.status == 200) {
      let name = res.headers['content-disposition'].split('filename=');
      name = name[1] ? name[1] : 'report';
      $JsDownload(res.data, `${name}.csv`);
      $notify(`<svg><use xlink:href="#yes--sprite"></use></svg> Download Success!`);
    } else {
      $notify(`<svg><use xlink:href="#yes--sprite"></use></svg> Download False!`);
    }
  });
};

const goFullReport = () => {
  user_info.has = false;
  user_info.data = null;
  refrash_key.value += 1;
};

const imageError = (e) => {
  e.target.src = defaultAvatar;
};

const sort = (val) => {
  sort_key.value = val.key;
  sort_val.value = val.val;
  page_current.value = 1;
  getTable();
};

const search = (keyword) => {
  search_keyword.value = keyword;
  page_current.value = 1;
  getTable();
};

const gotoPage = (page) => {
  page_current.value = page;
  getTable();
};

const updatePagenum = (item) => {
  page_size.value = item.id;
  page_current.value = 1;
  getTable();
};

const updateFilter = (newParams) => {
  Object.assign(params, newParams);
  page_current.value = 1;
  getTable();
  getSum();
};

const pickUser = (row) => {
  goProfileOverview(row);
};

const goProfile = (row) => {
  openProfile(row);
};

const emailto = (email) => {
  window.open(`mailto:${email}`);
};

const goProfileOverview = (row) => {
  user_info.has = true;
  user_info.data = row;
  Object.assign(date_range_summary, JSON.parse(JSON.stringify(date_range)));
  refrash_key.value += 1;
  selectTab(0);
};

const openProfile = (row) => {

  let user = row ? row : user_info.data;
  user.user_id = user.id;

  if (store.state.creator_data_open) {
    EventBus.emit('update-modal-creator', user);
  } else {
    setTimeout(() => {
      EventBus.emit('open-creator-modal', user);
    }, 10);
  }
  close();
};

// Lifecycle hooks
onMounted(() => {
  if (props.is_nested) return;
  EventBus.off('open-product-modal');
  EventBus.on('open-product-modal', (obj) => {
    sortlink_data.value = obj.init_sortlink;
    if (page_size.value === 0) page_size.value = select_pagenum[0].id;
    product_id.value = obj.product_id;
    start_date.value = moment(obj.start_date).format('YYYY-MM-DD');
    end_date.value = moment(obj.end_date).format('YYYY-MM-DD');
    date_range.start_date = obj.start_date;
    date_range.end_date = obj.end_date;
    date_range_summary.start_date = obj.start_date;
    date_range_summary.end_date = obj.end_date;

    data.value = [];
    product.value = obj;
    if (obj.data_info) {
      user_info.has = true;
      user_info.data = obj.data_info;
    } else {
      user_info.has = false;
      user_info.data = null;
    }
    console.log('user_info', user_info);
    open();
  });
});

const openWithCreator = (obj) => {
  sortlink_data.value = obj.init_sortlink || sortlink_data.value;
  if (page_size.value === 0) page_size.value = select_pagenum[0].id;
  product_id.value = obj.product_id;
  start_date.value = moment(obj.start_date).format('YYYY-MM-DD');
  end_date.value = moment(obj.end_date).format('YYYY-MM-DD');
  date_range.start_date = start_date.value;
  date_range.end_date = end_date.value;
  data.value = [];
  user_info.has = true;
  user_info.data = obj.creator;
  open();
};

// Expose methods for parent components
defineExpose({
  open,
  close,
  openWithCreator,
});
</script>

<style lang="scss">
.product-modal {
  width: 100%;
  height: 100%;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1002 !important;

  .modal-tabs {
    display: flex;
    border-bottom: 1px solid var(--black-10);
    padding: 0 16px;
    margin-top: 8px;

    .modal-tab {
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      padding: 8px 12px;
      font: var(--font-fg-14);
      color: var(--black-60);
      cursor: pointer;
      margin-bottom: -1px;

      &.active {
        border-bottom-color: var(--dark-blue);
        color: var(--dark-blue);
        font-weight: 600;
      }

      &:hover:not(.active) {
        color: var(--black-90);
      }
    }
  }

  header {
    .title {
      display: flex;
      align-items: center;

      span {
        border-left: solid 1px var(--black-20);
        padding: 0 6px;
        margin-left: 6px;
        display: flex;
        align-items: center;
        cursor: pointer;

        &:hover {
          background-color: var(--bg-08);
        }
      }
    }

    .avatar {
      padding-right: 10px;
      height: 32px;

      img {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 1px solid var(--black-40);
      }
    }
  }

  .navi-line-block{
    display: flex;
    margin-top: 16px;
    align-items: center;

    .title{
      display: flex;
      gap: 8px;
    }
  }

  .storefront-summary {
    margin-top: 16px;
  }

  .ui-modal-side--container {
    height: 100%;
    overflow: hidden;
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 100%;

    .sticky-head{
      position: sticky;
      top: 0;
      z-index: 20;
      background-color: var(--bg-01);
    }

    .box {
      overflow: hidden;
      display: grid;
      grid-template-rows: auto auto auto auto 1fr;
      grid-template-columns: 100%;
    }

    .product-info-box {
      margin-top: 16px;
      padding: 16px;
      display: flex;
      background: var(--bg-02);

      .product-flex-box {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .product-btn {
          border-radius: 100px;
          border: solid 1px var(--dark-blue);
          background-color: #fff;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          color: var(--dark-blue);
          cursor: pointer;
          min-width: 124px;
          max-height: 31.14px;
          margin-left: 10px;
          display: flex;
          justify-content: center;
        }
      }

      .image-wrap {
        width: 100px;
        height: 100px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        border-radius: 8px;
        border: 1px solid rgba(224, 224, 224, 1);

        img {
          display: block;
          position: absolute;
          left: 0%;
          top: 0%;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .product-info {
        margin-left: 16px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 1;

        .product-brand {
          font-weight: 500;
          font-size: 12px;
          line-height: 16px;
          color: var(--black-90);
        }

        .product-name {
          font: var(--font-demi-18);
          color: var(--black-90);
        }

        .product-data-box {
          margin-top: 16px;
          display: flex;

          .product-data {
            width: 20%;

            .text {
              font: var(--font-fg-12);
              color: var(--black-60);
            }

            .value {
              font: var(--font-demi-12);
              color: var(--black-90);
            }
          }

          .report {
            flex: 1;
            display: flex;
            justify-content: right;
            align-items: flex-end;
            font: var(--font-fg-12);
            color: var(--blue);
            cursor: pointer;
          }
        }
      }
    }

    .report-header {
      display: flex;
      align-items: center;
      padding: 16px 0;

      .ui-search {
        flex: 1;
        padding: 0 10px;

        .ui-search-input .input {
          max-width: unset;
        }
      }

      .left {
        display: flex;
        align-items: center;

        .title {
          font: var(--font-demi-20);
          color: var(--black-90);
        }

        .ui-button {
          display: inline-flex;
          align-items: center;

          .svg-icon--plus {
            height: 10px;
            width: 10px;
            margin-right: 5px;
          }
        }

        .ui-filter-dropdown {
          margin-left: 10px;
        }
      }

      .right {
        display: flex;
        align-items: center;

        .ui-date-picker {
          margin-right: 10px;
        }

        .ui-dropdown {
          margin-right: 10px;
        }

        .download-btn {
          color: var(--black-90);
          height: 32px;
          width: 32px;
          margin-left: 10px;
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            height: 16px;
            width: 16px;
            display: block;
          }
        }
      }

      .ui-dropdown {
        .ui-button {
          border-color: var(--black-10);
          background-color: var(--black-10);
        }
      }
    }

    .report-box {
      margin: 0 -8px 16px -8px;
      display: flex;

      & + div > .filter-results {
        margin: 0 0 16px;
      }

      .report-item-2 {
        width: calc(25% - 16px);
        background: white;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 8px;
        margin: 0 8px;
        position: relative;

        .top {
          padding: 12px 10px;
          display: flex;
          align-items: center;

          .value {
            font-size: 14px;
            color: var(--dark-blue);
            display: flex;
            align-items: center;
          }

          .name {
            font-size: 10px;
            color: var(--black-90);
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;

            span {
              color: var(--black-50);
              padding-left: 4px;
            }
          }

          svg {
            width: 16px;
            height: 16px;
            fill: var(--pink);
            margin-right: 5px;
          }
        }

        .avatar {
          height: 32px;
          padding-right: 10px;

          img {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 1px solid var(--black-40);
          }
        }

        .info {
          padding: 12px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.15);

          .text {
            font: var(--font-fg-12);
            color: var(--blue);
            display: flex;

            svg {
              width: 14px;
              height: 14px;
              fill: var(--blue);
              margin-right: 4px;
            }
          }
        }
      }

      &.is-link-user-box {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin: 0;
        .report-item-2 {
          width: 100%;
          margin: 0;
        }
      }
    }
  }

  .data-list {
    height: initial;
    //padding-bottom: 20px;
  }

  .ui-table {
    thead tr {
      border-top: 1px solid var(--black-20);
    }

    .name-text-box {
      .text-ellipsis {
        &:hover {
          text-decoration: underline;
        }
      }
    }

    .tbody-tr {
      cursor: pointer;
    }

    .text-ellipsis-link:hover {
      text-decoration: underline;
    }

    td p {
      font-size: 12px;
    }

    td .profile span {
      color: var(--black-90);
    }

    .tfoot td:nth-child(1) {
      text-align: center;
    }
  }
}
</style>
